import getApiHttpClient from '../../../httpClients/getApiHttpClient';
import type {
  Attribution,
  CfoTimeEntriesResponse,
  ClientItem,
  CreateClientBody,
  CreateStaffBody,
  HolidayItem,
  ListParams,
  OverviewCfo,
  PaginationMeta,
  StaffItem,
  TaskTypeItem,
  UpdateClientBody,
  UpdateStaffBody,
  VerifyResult,
} from './types';

export async function fetchOverview(date: string): Promise<{ cfos: OverviewCfo[] }> {
  const res = await getApiHttpClient().get<{ cfos: OverviewCfo[] }>('/api/v1/cfo/admin/overview', {
    params: { date },
  });
  return res.data;
}

export async function verifyIntegrations(userId?: string): Promise<{ results: VerifyResult[] }> {
  const res = await getApiHttpClient().post<{ results: VerifyResult[] }>(
    '/api/v1/cfo/admin/integrations/verify',
    userId ? { userId } : {},
  );
  return res.data;
}

export async function fetchStaff(
  params: ListParams,
): Promise<{ staff: StaffItem[]; pagination: PaginationMeta }> {
  const res = await getApiHttpClient().get<{ users: StaffItem[]; pagination: PaginationMeta }>(
    '/api/v1/cfo/users',
    { params },
  );
  return { staff: res.data.users, pagination: res.data.pagination };
}

export async function createStaff(body: CreateStaffBody): Promise<{ staff: StaffItem }> {
  const res = await getApiHttpClient().post<{ user: StaffItem }>('/api/v1/cfo/users', body);
  return { staff: res.data.user };
}

export async function updateStaff(
  id: string,
  body: UpdateStaffBody,
): Promise<{ staff: StaffItem }> {
  const res = await getApiHttpClient().patch<{ user: StaffItem }>(`/api/v1/cfo/users/${id}`, body);
  return { staff: res.data.user };
}

export async function fetchClients(
  params: ListParams,
): Promise<{ clients: ClientItem[]; pagination: PaginationMeta }> {
  const res = await getApiHttpClient().get<{ clients: ClientItem[]; pagination: PaginationMeta }>(
    '/api/v1/cfo/clients',
    { params },
  );
  return res.data;
}

export async function createClient(body: CreateClientBody): Promise<{ client: ClientItem }> {
  const res = await getApiHttpClient().post<{ client: ClientItem }>('/api/v1/cfo/clients', body);
  return res.data;
}

export async function updateClient(
  id: string,
  body: UpdateClientBody,
): Promise<{ client: ClientItem }> {
  const res = await getApiHttpClient().patch<{ client: ClientItem }>(
    `/api/v1/cfo/clients/${id}`,
    body,
  );
  return res.data;
}

export async function fetchTaskTypes(
  params: ListParams,
): Promise<{ taskTypes: TaskTypeItem[]; pagination: PaginationMeta }> {
  const res = await getApiHttpClient().get<{
    taskTypes: TaskTypeItem[];
    pagination: PaginationMeta;
  }>('/api/v1/cfo/task-types', { params });
  return res.data;
}

export async function syncTaskTypes(): Promise<{ syncedAt: string }> {
  const res = await getApiHttpClient().post<{ syncedAt: string }>('/api/v1/cfo/task-types/sync');
  return res.data;
}

export async function fetchCfoTimeEntries(
  date: string,
  userEmail: string,
): Promise<CfoTimeEntriesResponse> {
  const res = await getApiHttpClient().get<CfoTimeEntriesResponse>('/api/v1/cfo/time/entries', {
    params: { date, userEmail },
  });
  return res.data;
}

export async function fetchAttribution(month: string, params: ListParams): Promise<Attribution> {
  const res = await getApiHttpClient().get<Attribution>('/api/v1/cfo/capacity/attribution', {
    params: { month, ...params },
  });
  return res.data;
}

export async function fetchHolidays(year: number): Promise<{ holidays: HolidayItem[] }> {
  const res = await getApiHttpClient().get<{ holidays: HolidayItem[] }>(
    '/api/v1/cfo/capacity/holidays',
    { params: { year } },
  );
  return res.data;
}

export async function createHoliday(body: {
  country: string;
  date: string;
  name: string;
}): Promise<{ holiday: HolidayItem }> {
  const res = await getApiHttpClient().post<{ holiday: HolidayItem }>(
    '/api/v1/cfo/capacity/holidays',
    body,
  );
  return res.data;
}

export async function removeHoliday(id: string): Promise<void> {
  await getApiHttpClient().delete(`/api/v1/cfo/capacity/holidays/${id}`);
}
